import { PropsWithChildren } from 'react';

import {
  ChakraBadge,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  EyeSlash,
  FileClip,
  ProviderAvatarBlock,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  NoteAttachmentStatus,
  NoteStatus,
  PatientNoteListItemType,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { formatDateWithOrdinal } from '~/lib/utils';

import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

const { paragraphBold, caption } = EquipUITheme.typography;
const { critical, red, tertiary, neutral } = EquipUIFireflyV2Theme.colors;
interface PatientNotesListItemProps {
  index: number;
  note: PatientNoteListItemType;
  onClickNoteListItem?: (item: number) => void;
  prevNote?: PatientNoteListItemType;
  isSelected: boolean;
}

interface BadgeComponentProps extends PropsWithChildren {
  backgroundColor?: string;
  color?: string;
  id?: string;
  margin?: string;
}

interface LegalName {
  firstName: string;
  lastName: string;
}

const getMonthAndYear = (dateISO: string) =>
  dateISO ? DateTime.fromISO(dateISO).toFormat('MMMM yyyy') : null;

const BadgeComponent = ({
  backgroundColor,
  children,
  color,
  id,
  margin,
}: BadgeComponentProps) => (
  <ChakraBadge
    alignItems="center"
    backgroundColor={backgroundColor}
    borderRadius="100px"
    color={color || neutral.primary}
    display="flex"
    fontSize="10px"
    fontWeight="700"
    height="14px"
    id={`${id}__badge`}
    justifyContent="center"
    margin={margin || '0 8px 0 0'}
    textTransform="none"
  >
    {children}
  </ChakraBadge>
);
const PatientNotesListItem = ({
  index,
  note,
  onClickNoteListItem,
  prevNote,
  isSelected,
}: PatientNotesListItemProps) => {
  const {
    activityDateTime,
    attachments,
    createdByUser,
    isConfidential,
    noteExternalId,
    noteStatus,
    noteType,
    title,
  } = note;

  const TOP_LEVEL_ID = `patient-notes-list-item__${noteExternalId}`;
  const uploadedAttachments = (attachments ?? []).filter(
    (attachment) =>
      attachment.statusCode === null ||
      attachment.statusCode === NoteAttachmentStatus.UPLOADED,
  );
  const {
    firstName: providerFirstName,
    lastName: providerLastName,
    providerTypeAtNoteCreation,
  } = createdByUser || {};
  const getBorderColor = () => {
    if (isSelected) {
      return 'primary.50';
    }
    if (noteStatus === NoteStatus.VOID) {
      return 'red.25';
    }
    return 'neutral.line';
  };

  const legalName: LegalName = {
    firstName: providerFirstName,
    lastName: providerLastName,
  };

  const avatarSection = (
    <ChakraFlex alignItems="center">
      <ProviderAvatarTooltipWrapper
        actionTime={activityDateTime}
        isRoleAtAction
        placement="bottom"
        providerExternalId={createdByUser?.externalId}
        shouldShowDisplayName={false}
      >
        <ProviderAvatarBlock
          avatarOnly
          avatarUrl={createdByUser?.avatar?.thumbnailUrl}
          id={`${TOP_LEVEL_ID}__avatar-content`}
          legalName={legalName}
          variant="compact"
        />
      </ProviderAvatarTooltipWrapper>
      {providerTypeAtNoteCreation && (
        <ChakraBox
          {...caption}
          color="neutral.secondary"
          id={`${TOP_LEVEL_ID}__provider-type`}
          textTransform="capitalize"
        >
          {providerTypeAtNoteCreation}
        </ChakraBox>
      )}
    </ChakraFlex>
  );

  return (
    <>
      {index !== undefined &&
        (index === 0 ||
          getMonthAndYear(activityDateTime) !==
            getMonthAndYear(prevNote?.activityDateTime)) && (
          <ChakraBox
            color="neutral.secondary"
            cursor="pointer"
            fontSize="12px"
            lineHeight="18px"
            marginBottom="12px"
          >
            {getMonthAndYear(activityDateTime)}
          </ChakraBox>
        )}
      <ChakraBox
        background={isSelected ? 'primary.10' : 'neutral.background.primary'}
        borderColor={getBorderColor()}
        borderRadius="16px"
        borderStyle="solid"
        borderWidth={isSelected ? '2px' : '1px'}
        marginBottom="16px"
        onClick={() => {
          onClickNoteListItem(index);
        }}
        padding="16px"
      >
        <ChakraFlex
          alignItems="start"
          justifyContent="space-between"
          marginBottom="16px"
        >
          <ChakraFlex alignItems="start">
            {noteStatus === NoteStatus.DRAFT && (
              <BadgeComponent
                backgroundColor={tertiary.marigold.base}
                id={`${TOP_LEVEL_ID}__draft`}
              >
                <ChakraFlex alignItems="center" justifyContent="space-between">
                  <Edit fill={neutral.primary} height="8px" width="8px" />
                  <ChakraText marginX="2px">Draft</ChakraText>
                </ChakraFlex>
              </BadgeComponent>
            )}
            {noteStatus === NoteStatus.VOID && (
              <BadgeComponent
                backgroundColor={critical.base}
                id={`${TOP_LEVEL_ID}__void`}
              >
                Void
              </BadgeComponent>
            )}
            <ChakraBox
              {...paragraphBold}
              color="neutral.primary"
              id={`${TOP_LEVEL_ID}__title`}
              lineHeight="16.8px"
            >
              {title ?? 'Title'}
            </ChakraBox>
          </ChakraFlex>
          <ChakraBox
            {...caption}
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__activity-date`}
            whiteSpace="nowrap"
          >
            {formatDateWithOrdinal(activityDateTime)}
          </ChakraBox>
        </ChakraFlex>

        <ChakraFlex alignItems="center" justifyContent="space-between">
          {avatarSection}
          <ChakraFlex alignItems="center" marginLeft="auto">
            {isConfidential && (
              <BadgeComponent
                backgroundColor={red[10]}
                color={red.shade}
                id={`${TOP_LEVEL_ID}__confidential`}
              >
                <ChakraFlex alignItems="center" justifyContent="space-between">
                  <EyeSlash fill={red.shade} height="8px" width="8px" />
                  <ChakraText marginLeft="4px">Sensitive</ChakraText>
                </ChakraFlex>
              </BadgeComponent>
            )}
            {noteType && (
              <BadgeComponent
                backgroundColor="neutral.background.secondary"
                color="neutral.primary"
                id={`${TOP_LEVEL_ID}__note-type`}
                margin="0"
              >
                {noteType}
              </BadgeComponent>
            )}

            {Boolean(uploadedAttachments.length) && (
              <ChakraBox id={`${TOP_LEVEL_ID}__attachments`}>
                <FileClip
                  data-testid={`${TOP_LEVEL_ID}__attachments-clip-icon`}
                  fill={neutral.secondary}
                  height="14px"
                  width="16px"
                />
              </ChakraBox>
            )}
          </ChakraFlex>
        </ChakraFlex>
      </ChakraBox>
    </>
  );
};

export default PatientNotesListItem;
